// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Importing a folder of old estimates in one go.
 *
 * One at a time is where somebody stops at three jobs, and three is not enough
 * for the portfolio views to say anything worth reading — which is the reason
 * they were asked to import at all.
 *
 * Paced to the server's own limit rather than sprinting into a refusal, and it
 * knows the difference between a file it cannot read and an account it cannot
 * bill: the first costs you that file, the second stops the run so nobody
 * watches nine more failures to learn one fact.
 */

import React, { useRef, useState } from "react";
import type { Theme } from "../lib/theme";
import { importEstimateFile, estimateSubtotal } from "../lib/estimateImport";
import {
  fetchOrgCostCodes, catalogForPrompt, validCodeSet, unmappedSubcategories,
} from "../lib/orgCostCodes";
import {
  msUntilAllowed, classifyImportFailure, queueSummary, estimateRunMs, humanDuration,
  type QueueItem,
} from "../lib/importQueue";

const money = (n: number) => "$" + Math.round(n).toLocaleString();
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export default function BulkImportDialog({
  T, FONT, orgId, onClose, onCreate, onDone,
}: {
  T: Theme;
  FONT: string;
  orgId?: string | null;
  onClose: () => void;
  /** Saves one parsed estimate; resolves to the new project id. */
  onCreate: (state: any, fileName: string) => Promise<string | null>;
  /** Called once when the run finishes and anything landed. */
  onDone: (importedCount: number) => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [stopped, setStopped] = useState<string | null>(null);
  const [waitingMs, setWaitingMs] = useState(0);
  const cancelRef = useRef(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const pick = (list: FileList | null) => {
    if (!list?.length) return;
    const picked = Array.from(list).filter((f) => f.size <= 3 * 1024 * 1024);
    const tooBig = Array.from(list).length - picked.length;
    setFiles(picked);
    setItems(picked.map((f) => ({ name: f.name, status: "pending" as const })));
    setFinished(false);
    setStopped(tooBig > 0 ? `${tooBig} file${tooBig === 1 ? "" : "s"} skipped — over the 3 MB limit.` : null);
  };

  const run = async () => {
    setRunning(true);
    setStopped(null);
    cancelRef.current = false;

    const catalog = await fetchOrgCostCodes(orgId);
    const prompt = catalogForPrompt(catalog.all);
    const valid = validCodeSet(catalog.all);

    const startedAt: number[] = [];
    const setItem = (i: number, patch: Partial<QueueItem>) =>
      setItems((prev) => prev.map((it, n) => (n === i ? { ...it, ...patch } : it)));

    let imported = 0;

    for (let i = 0; i < files.length; i++) {
      if (cancelRef.current) {
        setItems((prev) => prev.map((it) => (it.status === "pending" ? { ...it, status: "skipped" } : it)));
        break;
      }

      // Wait out the server's window rather than earning a refusal.
      const wait = msUntilAllowed(startedAt, Date.now());
      if (wait > 0) {
        setWaitingMs(wait);
        const until = Date.now() + wait;
        while (Date.now() < until && !cancelRef.current) {
          setWaitingMs(Math.max(0, until - Date.now()));
          await sleep(Math.min(500, until - Date.now()));
        }
        setWaitingMs(0);
        if (cancelRef.current) continue;
      }

      setItem(i, { status: "working" });

      let attempt = 0;
      for (;;) {
        try {
          const { supabase } = await import("../lib/supabase");
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) throw new Error("Please sign in to import an estimate.");

          const { projectState } = await importEstimateFile(
            files[i], session.access_token, undefined, prompt,
            // Only a request that actually goes out costs a rate-limit slot.
            // Counting attempts made a run of unreadable files pause a minute
            // for calls the server never saw.
            () => startedAt.push(Date.now()),
          );
          const unmapped = unmappedSubcategories(projectState, valid)
            .reduce((n, u) => n + u.lines, 0);

          if (!projectState.projectName) projectState.projectName = files[i].name.replace(/\.[^.]+$/, "");
          const id = await onCreate(projectState, files[i].name);
          if (!id) throw new Error("The estimate read correctly but could not be saved.");

          imported++;
          setItem(i, { status: "done", subtotal: estimateSubtotal(projectState), unmapped });
          break;
        } catch (e: any) {
          const verdict = classifyImportFailure(e?.message || "");
          if (verdict.action === "retry" && attempt < 1) {
            attempt++;
            // One retry, after the window, which is the only thing that helps.
            await sleep(Math.max(2_000, msUntilAllowed(startedAt, Date.now())));
            continue;
          }
          if (verdict.action === "stop") {
            setItem(i, { status: "failed", error: verdict.reason });
            setItems((prev) => prev.map((it) => (it.status === "pending" ? { ...it, status: "skipped" } : it)));
            setStopped(verdict.reason);
            i = files.length;
            break;
          }
          setItem(i, { status: "failed", error: verdict.reason });
          break;
        }
      }
    }

    setRunning(false);
    setFinished(true);
    if (imported > 0) onDone(imported);
  };

  const label: React.CSSProperties = {
    fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase",
    color: T.textWeak, fontWeight: 700,
  };
  const dot = (s: QueueItem["status"]) =>
    s === "done" ? T.positive : s === "failed" ? T.negative
    : s === "working" ? T.borderAccent : T.textWeak;

  return (
    <div onClick={() => !running && onClose()}
      style={{ position: "fixed", inset: 0, zIndex: 300, background: T.bgModalBackdrop,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: T.bgModal, border: `1px solid ${T.borderSecondary}`, borderRadius: 10,
          width: "min(640px, 100%)", maxHeight: "88vh", display: "flex", flexDirection: "column",
          fontFamily: FONT, overflow: "hidden" }}>

        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.borderPrimary}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary }}>Import past projects</div>
          <div style={{ fontSize: 11, color: T.textWeak, marginTop: 3, lineHeight: 1.55 }}>
            Pick as many old estimates as you like. Each becomes its own project, mapped onto your
            cost codes. Five or six jobs is where the portfolio views start telling you something.
          </div>
        </div>

        <div style={{ padding: "18px 20px", overflowY: "auto", flex: 1 }}>
          {items.length === 0 ? (
            <>
              <button onClick={() => fileRef.current?.click()}
                style={{ border: `1px dashed ${T.borderSecondary}`, borderRadius: 6, background: "transparent",
                  color: T.textMuted, padding: "28px 16px", fontSize: 12, fontFamily: FONT,
                  cursor: "pointer", width: "100%" }}>
                Choose estimate files (.xlsx, .csv, .pdf) — select several at once
              </button>
              {stopped && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 10 }}>{stopped}</div>}
            </>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
                <span style={label}>{running || finished ? "Progress" : `${items.length} files`}</span>
                <span style={{ fontSize: 12, color: T.textSecondary, flex: 1 }}>
                  {running || finished
                    ? queueSummary(items)
                    : `Takes ${humanDuration(estimateRunMs(items.length))} — the importer allows five files a minute.`}
                </span>
              </div>

              {waitingMs > 0 && (
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 10 }}>
                  Pausing {Math.ceil(waitingMs / 1000)}s to stay inside the rate limit…
                </div>
              )}

              {stopped && (
                <div style={{ fontSize: 11, color: T.negative, background: `${T.negative}14`,
                  border: `1px solid ${T.negative}44`, borderRadius: 6, padding: "9px 12px",
                  marginBottom: 10, lineHeight: 1.55 }}>
                  {stopped}
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {items.map((it, i) => (
                  <div key={`${it.name}-${i}`}
                    style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 8px",
                      borderRadius: 3, background: i % 2 ? "transparent" : `${T.bgPanel}66` }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot(it.status),
                      flexShrink: 0, opacity: it.status === "pending" ? 0.4 : 1 }} />
                    <span style={{ flex: 1, fontSize: 12, color: T.textSecondary, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.name}>
                      {it.name}
                    </span>
                    {it.status === "done" && (
                      <>
                        {it.unmapped ? (
                          <span style={{ fontSize: 10, color: "#f6ad55" }} title="Lines with no matching cost code">
                            {it.unmapped} unassigned
                          </span>
                        ) : null}
                        <span style={{ fontSize: 12, fontWeight: 600, color: T.textPrimary,
                          fontVariantNumeric: "tabular-nums" }}>{money(it.subtotal || 0)}</span>
                      </>
                    )}
                    {it.status === "working" && <span style={{ fontSize: 10, color: T.borderAccent }}>reading…</span>}
                    {it.status === "skipped" && <span style={{ fontSize: 10, color: T.textWeak }}>not attempted</span>}
                    {it.status === "failed" && (
                      <span style={{ fontSize: 10, color: T.negative, maxWidth: 260, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.error}>{it.error}</span>
                    )}
                  </div>
                ))}
              </div>

              {finished && items.some((i) => i.status === "done") && (
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 12, lineHeight: 1.6 }}>
                  Check a total or two against the files you imported. If they match, Portfolio Insights
                  now has your own work in it.
                </div>
              )}
            </>
          )}
          <input ref={fileRef} type="file" multiple accept=".xlsx,.xls,.xlsm,.csv,.pdf"
            onChange={(e) => { pick(e.target.files); e.currentTarget.value = ""; }}
            style={{ display: "none" }} />
        </div>

        <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.borderPrimary}`,
          display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {running ? (
            <button onClick={() => { cancelRef.current = true; }}
              style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted,
                borderRadius: 3, padding: "7px 14px", fontSize: 12, fontFamily: FONT, cursor: "pointer" }}>
              Stop after this file
            </button>
          ) : (
            <button onClick={onClose}
              style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted,
                borderRadius: 3, padding: "7px 14px", fontSize: 12, fontFamily: FONT, cursor: "pointer" }}>
              {finished ? "Done" : "Cancel"}
            </button>
          )}
          {!finished && (
            <button onClick={run} disabled={running || items.length === 0}
              style={{ background: items.length && !running ? T.borderAccent : T.borderSecondary, border: "none",
                color: items.length && !running ? T.textOnGold : T.textWeak, fontWeight: 700, borderRadius: 3,
                padding: "7px 16px", fontSize: 12, fontFamily: FONT,
                cursor: items.length && !running ? "pointer" : "default" }}>
              {running ? "Importing…" : `Import ${items.length || ""}`.trim()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
